import { Hub, HubCapsule } from '@aws-amplify/core';
import { AuthState } from '@aws-amplify/ui-components';
import { createEffect, createEvent, sample } from 'effector';
import { t } from 'i18next';
import { cloneDeep, isEqual, pick } from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global } from 'models/store-global';

import AlertsService from 'services/AlertsService';
import TempUsersService from 'services/TempUsersService';

import { type MyStore } from 'types/Store';

$$auth.initFx();

sample({
    clock: [
        $$auth.initFx.failData,
        $$auth.resendSignUpFx.failData,
        // $$auth.currentUserPoolUserFx.failData,
        $$auth.currentAuthenticatedUserFx.failData,
        // $$auth.signOutFx.failData,
        // $$auth.signInFx.failData,
        $$auth.federatedSignInFx.failData,
        // $$auth.signUpFx.failData,
        $$auth.confirmSignUpFx.failData,
        // $$auth.forgotPasswordFx.failData,
        // $$auth.forgotPasswordSubmitFx.failData,
        // $$auth.completeNewPasswordFx.failData,
        // $$auth.changePasswordFx.failData,
        $$auth.sendCustomChallengeAnswerFx.failData,
        // $$auth.currentSessionFx.failData,
    ],
    target: createEffect(AlertsService.handleError),
});

sample({
    clock: $$auth.resendSignUpFx.done,
    fn: () => t('signIn.email_was_resent'),
    target: createEffect(AlertsService.showSuccess),
});

sample({ source: $global.map((state) => state.auth?.authState || null), target: $$auth.$authState });
sample({ source: $global.map((state) => state.auth?.isRelevantSigned || false), target: $$auth.$isRelevantSigned });
sample({ source: $global.map((state) => state.temp?.initialized || false), target: $$auth.$tempInitialized });
sample({ source: $global.map((state) => state.auth?.user || null), target: $$auth.$user });
sample({
    source: $global.map((state) => state.auth?.authState === AuthState.SignedIn && state.auth?.user != null),
    target: $$auth.$isAuthenticated,
});

sample({
    source: $global,
    clock: $$auth.currentUserPoolUserFx.doneData,
    fn: (global, user): MyStore => ({
        ...global,
        auth: { ...(global.auth || {}), user, authState: AuthState.SignedIn },
    }),
    target: $global,
});

sample({
    source: $global,
    clock: $$auth.currentUserPoolUserFx.failData,
    fn: (global) => {
        delete global.auth?.user;
        delete global.auth?.authState;
        return global;
    },
    target: $global,
});

sample({
    source: {
        currentParticipant: $$auth.$currentParticipant,
        user: $$auth.$user,
        isAuthenticated: $$auth.$isAuthenticated,
    },
    fn: ({ user, currentParticipant, isAuthenticated }) =>
        isAuthenticated && Boolean(user)
            ? currentParticipant?.systemRoles?.includes('admin')
                ? ('admin' as const)
                : currentParticipant?.systemRoles?.includes('advisor')
                ? ('advisor' as const)
                : TempUsersService.isTempUser(user?.attributes?.email || '')
                ? ('tempUser' as const)
                : ('user' as const)
            : ('guest' as const),
    target: $$auth.$userRole,
});

sample({
    source: $global,
    clock: $$auth.$initialized,
    fn: (store, value): MyStore => ({
        ...store,
        initialized: value,
    }),
    target: $global,
});

sample({
    source: $global,
    clock: $$auth.$userRole,
    fn: (store, value): MyStore => ({
        ...store,
        auth: {
            ...(store.auth || {}),
            userRole: value,
        },
    }),
    target: $global,
});

sample({
    source: {
        global: $global,
        isAuthenticated: $$auth.$isAuthenticated,
        isRelevantSigned: $$auth.$isRelevantSigned,
        userRole: $$auth.$userRole,
        initialized: $$auth.$initialized,
    },
    filter: ({ global, ...value }) => !isEqual(global.auth, { ...(global.auth || {}), ...value }),
    fn: ({ global, ...value }): MyStore => ({ ...global, auth: { ...(global.auth || {}), ...value } }),
    target: $global,
});

sample({ source: $$auth.$isAuthenticated, filter: Boolean, target: $$auth.checkIsRelevantSignedFx });
sample({ clock: $$auth.checkIsRelevantSignedFx.done, target: $$api.participants.getMeFx });
sample({
    source: $global,
    clock: $$api.participants.getMeFx.done,
    fn: (store): MyStore => ({
        ...store,
        temp: {
            ...(store.temp || {}),
            initialized: true,
        },
    }),
    target: $global,
});

const hubCatchedAuth = createEvent<HubCapsule>();

sample({
    source: $global,
    clock: hubCatchedAuth,
    filter: (_store, { payload }) => payload.event === 'signOut',
    fn: (store): MyStore => ({
        ...store,
        auth: {
            isAuthenticated: false,
        },
    }),
    target: $global,
});

const keysToSuppress = [
    'auth',
    'shouldSignIn',
    'username',
    'password',
    'authData',
    'loginRole',
    'alerts',
    'startAssessment',
];

sample({
    source: $global,
    clock: hubCatchedAuth,
    filter: (_, { payload }) => payload.event === 'signOut',
    fn: (store): MyStore => pick(cloneDeep(store), keysToSuppress),
    target: $global,
});

sample({
    clock: hubCatchedAuth,
    filter: ({ payload }) => payload.event !== 'signOut',
    target: $$auth.currentUserPoolUserFx,
});

Hub.listen('auth', hubCatchedAuth);

$$auth.currentUserPoolUserFx();
